import React, { useState, useEffect } from "react";
import { Box, Grid, TextField } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import OperationalDays from "./OperationalDays";

const StoreDetails = ({ profileData, setProfileData }) => {
  const [openTime, setOpenTime] = useState(
    dayjs(profileData.customData.storePickupOpenTime, "HH")
  );
  const [closeTime, setCloseTime] = useState(
    dayjs(profileData.customData.storePickupCloseTime, "HH")
  );
  const [phoneNumber, setPhoneNumber] = useState(
    profileData.ondcSellerInfo.storePocPhoneNumber || ""
  );
  const [error, setError] = useState(false);

  useEffect(() => {
    setOpenTime(dayjs(profileData.customData.storePickupOpenTime, "HH"));
    setCloseTime(dayjs(profileData.customData.storePickupCloseTime, "HH"));
  }, [profileData]);

  const handleTimeChange = (field, newValue) => {
    if (!newValue || !newValue.isValid()) {
      return;
    }

    if (field === "storePickupOpenTime") {
      if (newValue.isBefore(closeTime)) {
        setOpenTime(newValue);
        setProfileData((prevState) => ({
          ...prevState,
          customData: {
            ...prevState.customData,
            storePickupOpenTime: newValue.format("HH"),
          },
        }));
      } else {
        alert("Open time must be less than close time.");
        setOpenTime(dayjs(profileData.customData.storePickupOpenTime, "HH"));
      }
    } else if (field === "storePickupCloseTime") {
      if (newValue.isAfter(openTime)) {
        setCloseTime(newValue);
        setProfileData((prevState) => ({
          ...prevState,
          customData: {
            ...prevState.customData,
            storePickupCloseTime: newValue.format("HH"),
          },
        }));
      } else {
        alert("Close time must be greater than open time.");
        setCloseTime(dayjs(profileData.customData.storePickupCloseTime, "HH"));
      }
    }
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    if (/^\d{0,10}$/.test(value)) {
      setPhoneNumber(value);

      const isValidIndianPhoneNumber = /^[6-9]\d{9}$/.test(value);
      setError(!isValidIndianPhoneNumber );

      if (isValidIndianPhoneNumber) {
        setProfileData((prevState) => ({
          ...prevState,
          ondcSellerInfo: {
            ...prevState.ondcSellerInfo,
            storePocPhoneNumber: value,
          },
        }));
      }
    }
  };

  const handleInvalidNumber = () => {
    if (error) {
      setPhoneNumber(profileData.ondcSellerInfo.storePocPhoneNumber)
      setProfileData((prevState) => ({
        ...prevState,
        ondcSellerInfo: {
          ...prevState.ondcSellerInfo,
          storePocPhoneNumber: profileData.ondcSellerInfo.storePocPhoneNumber,
        },
      }));
      setError(prevState => !prevState);
    }
  }

  return (
    <Box my={3}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Store Description"
            value={profileData.customData.storeDescription || ""}
            onChange={(e) =>
              setProfileData((prevState) => ({
                ...prevState,
                customData: {
                  ...prevState.customData,
                  storeDescription: e.target.value,
                },
              }))
            }
          />
        </Grid>

        <Grid item xs={12}>
          <OperationalDays
            operationalDays={profileData?.customData?.storeOperationalDays.split(
              ","
            )}
            setOperationalDays={(updatedDays) =>
              setProfileData((prevState) => ({
                ...prevState,
                customData: {
                  ...prevState.customData,
                  storeOperationalDays: updatedDays,
                },
              }))
            }
          />
        </Grid>

        <Grid item xs={12} container spacing={2}>
          <Grid item xs={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                label="Store Pickup Open Time (HH)"
                value={openTime}
                maxTime={dayjs(closeTime).subtract(1, "hour")}
                onChange={(newValue) =>
                  handleTimeChange("storePickupOpenTime", newValue)
                }
                ampm={false}
                views={["hours"]}
                renderInput={(params) => <TextField fullWidth {...params} />}
                onBlur={() =>
                  setOpenTime(
                    dayjs(profileData.customData.storePickupOpenTime, "HH")
                  )
                }
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={6} >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                label="Store Pickup Close Time (HH)"
                value={closeTime}
                minTime={dayjs(openTime).add(1, "hour")}
                onChange={(newValue) =>
                  handleTimeChange("storePickupCloseTime", newValue)
                }
                ampm={false}
                views={["hours"]}
                renderInput={(params) => <TextField fullWidth {...params} />}
                onBlur={() =>
                  setCloseTime(
                    dayjs(profileData.customData.storePickupCloseTime, "HH")
                  )
                }
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
       
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Name"
            value={profileData?.ondcSellerInfo?.storePocName || ""}
            onChange={(e) =>
              setProfileData((prevState) => ({
                ...prevState,
                ondcSellerInfo: {
                  ...prevState.ondcSellerInfo,
                  storePocName: e.target.value,
                },
              }))
            }
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Phone Number"
            type="tel"
            value={phoneNumber}
            onChange={handlePhoneNumberChange}
            onBlur = {handleInvalidNumber}
            error={error}
            helperText={
              error
                ? "Enter a valid 10-digit Indian phone number."
                : ""
            }
            inputProps={{
              pattern: "[6-9]{1}[0-9]{9}",
              maxLength: 10,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Email"
            value={profileData?.ondcSellerInfo?.storePocEmailId || ""}
            onChange={(e) =>
              setProfileData((prevState) => ({
                ...prevState,
                ondcSellerInfo: {
                  ...prevState.ondcSellerInfo,
                  storePocEmailId: e.target.value,
                },
              }))
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default StoreDetails;
